import { makeStyles, tokens } from '@fluentui/react-components';
import { useAuth, useT } from '@powerportalspro/react';
import {
  DeleteRecordGridButton,
  GridToolbarItems,
  MoneyEdit,
  NewRecordGridButton,
  OpenRecordGridButton,
  SubGrid,
  TabPanel,
  Tabs,
  TextEdit,
  WizardRecordPage,
  type ViewDefinition,
} from '@powerportalspro/react-fluent';

// Same 1:N relationship the Blazor `SubGridMainDemo` uses — the parent
// `<RecordContext>` (provided by the docs sample envelope) points at an
// account record, and this SubGrid renders its related contacts via the
// `contact_customer_accounts` relationship.
//
// Custom views mirror the Blazor demo's `AccountAndContactCustomViews` —
// same view ids on both stacks so the dropdown selection stays consistent
// if a user toggles between the Blazor and React tabs. `viewIds` is set
// alongside `customViewDefinitions` so the view-picker dropdown is locked
// to just these two — the Dataverse system views for `contact` don't
// appear, matching how the Blazor demo passes the same `ViewIds` filter.
//
// The "Active Contacts" view needs no filter: the contact table's row
// security already keeps every query to the demo's sample contacts and the
// signed-in user's own. The "My Contacts" view restricts to just the
// current user's records. The auth hook
// resolves the user id (contactid for portal users); anonymous viewers
// see `Guid.Empty` as the filter value, which matches the Blazor
// `currentUserId?.ToString() ?? Guid.Empty.ToString()` fallback.
const ALL_CONTACTS_VIEW_ID = '28299C6F-EBC0-4206-9E11-A373D4C9891F';
const MY_CONTACTS_VIEW_ID = '25999A64-A871-4200-A38C-22ACD531F1A2';
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

// Tab labels mirror Blazor's `EditContactForm.razor` localization keys
// (`tab1.title` / `tab2.title` under the form's full type name). Both
// stacks read from the same JSON so the labels stay in lockstep.
const EDIT_FORM_STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Grids.SubGrid.EditContactForm';

function buildAllContactsView(): ViewDefinition {
  return {
    id: ALL_CONTACTS_VIEW_ID,
    tableName: 'contact',
    fetchXml: `<fetch>
      <entity name="contact">
        <attribute name="emailaddress1" />
        <attribute name="fullname" />
        <attribute name="mobilephone" />
        <attribute name="parentcustomerid" />
        <attribute name="ownerid" />
        <attribute name="ppp_owningportaluserid" />
        <attribute name="ppp_age" />
        <attribute name="birthdate" />
        <order attribute="fullname" />
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'fullname', width: 200 },
      { columnName: 'mobilephone', width: 200 },
      { columnName: 'emailaddress1', width: 225 },
      { columnName: 'parentcustomerid', width: 200 },
      { columnName: 'ppp_age', width: 100 },
      { columnName: 'birthdate', width: 125 },
    ],
  };
}

function buildMyContactsView(currentUserId: string): ViewDefinition {
  return {
    id: MY_CONTACTS_VIEW_ID,
    tableName: 'contact',
    fetchXml: `<fetch>
      <entity name="contact">
        <attribute name="emailaddress1" />
        <attribute name="fullname" />
        <attribute name="mobilephone" />
        <attribute name="parentcustomerid" />
        <attribute name="ownerid" />
        <attribute name="ppp_owningportaluserid" />
        <attribute name="ppp_age" />
        <filter type="or">
          <condition attribute="ppp_owningportaluserid" operator="eq" value="${currentUserId}" />
          <condition attribute="ownerid" operator="eq" value="${currentUserId}" />
        </filter>
        <order attribute="fullname" />
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'fullname', width: 200 },
      { columnName: 'mobilephone', width: 200 },
      { columnName: 'emailaddress1', width: 225 },
      { columnName: 'parentcustomerid', width: 200 },
      { columnName: 'ppp_age', width: 100 },
    ],
  };
}

// `lazyMount={false}` mounts both tabs with the form, so every edit inside
// the open dialog registers with the surrounding `<RecordContext>` for
// dirty / validate cascade regardless of which tab the user has visible.
// Switching tabs only hides the other one — its controls stay in the React
// tree, so a user can fill out fields on tab 2, switch back to tab 1 to fix
// a typo, switch back, and find their tab-2 values intact.
const useEditFormTabStyles = makeStyles({
  panels: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
});

function EditContactForm() {
  const t = useT();
  const styles = useEditFormTabStyles();
  return (
    <Tabs lazyMount={false}>
      <TabPanel value="general" header={t(`${EDIT_FORM_STRINGS_BASE}.tab1.title`)}>
        <div className={styles.panels}>
          <TextEdit columnName="firstname" />
          <TextEdit columnName="middlename" />
          <TextEdit columnName="lastname" />
        </div>
      </TabPanel>
      <TabPanel value="other" header={t(`${EDIT_FORM_STRINGS_BASE}.tab2.title`)}>
        <div className={styles.panels}>
          <MoneyEdit columnName="annualincome" />
          <TextEdit columnName="telephone1" type="tel" />
        </div>
      </TabPanel>
    </Tabs>
  );
}

// `<NewRecordGridButton>` switches to wizard mode automatically when any
// direct child is a `<WizardRecordPage>` (mirrors Blazor's
// `FormType.WizardForm` discriminator without needing a separate prop).
// `<OpenRecordGridButton location="panel">` opens a side panel instead of
// a centered dialog — matches Blazor's `Location="DialogLocation.Right"`
// on the open button. Same fields the Blazor `NewContactForm` /
// `EditContactForm` render.
export function SubGridMainSample() {
  const auth = useAuth();
  const currentUserId =
    auth.status === 'authenticated' ? auth.user.userId ?? EMPTY_GUID : EMPTY_GUID;

  const customViews: ViewDefinition[] = [
    buildAllContactsView(),
    buildMyContactsView(currentUserId),
  ];

  return (
    <SubGrid
      tableName="contact"
      relationshipName="contact_customer_accounts"
      pageSize={10}
      customViewDefinitions={customViews}
      viewIds={[ALL_CONTACTS_VIEW_ID, MY_CONTACTS_VIEW_ID]}
      defaultViewId={ALL_CONTACTS_VIEW_ID}
    >
      <GridToolbarItems>
        <NewRecordGridButton>
          <WizardRecordPage>
            <TextEdit columnName="firstname" />
            <TextEdit columnName="middlename" />
            <TextEdit columnName="lastname" />
          </WizardRecordPage>
          <WizardRecordPage>
            <MoneyEdit columnName="annualincome" />
            <TextEdit columnName="telephone1" type="tel" />
          </WizardRecordPage>
        </NewRecordGridButton>
        <OpenRecordGridButton location="panel">
          <EditContactForm />
        </OpenRecordGridButton>
        <DeleteRecordGridButton />
      </GridToolbarItems>
    </SubGrid>
  );
}
